/**
 * Draft state + persistence for the unified Agent Definition page (DR1).
 *
 * - No separate Publish CTA: Save persists State (draft | private | public | archived).
 * - Leaving Draft (or saving an open draft version after Copy) auto-publishes the version.
 * - When agent State is private/public/archived: identity + About fields are read-only;
 *   only State may change among those three. Blueprint is editable only while a draft
 *   version is open (catalog Copy).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createVersion,
  getAgent,
  listIndustryPacks,
  listVersions,
  publishVersion,
  updateAgent,
  updateVersion,
} from '@/services/aiStudioAgentService';
import type {
  AiStudioAgent,
  AiStudioAgentVersion,
  AiStudioIndustryPack,
  ExpectedFieldRow,
  StudioAgentVisibility,
  StudioComputeClass,
  StudioDecisionMode,
  StudioExecutionMode,
  UpdateVersionRequest,
} from '@/types/aiStudioAgent';
import { STUDIO_DECISION_MODES } from '@/types/aiStudioAgent';
import { deriveEnabledStages } from './deriveEnabledStages';
import { normalizeAutonomyFences } from './GovernanceFencesEditor';
import { slugifyAgentCode } from './slugifyAgentCode';

interface AgentDraft {
  name: string;
  /** Preview of persisted code; auto-synced from name while visibility is draft. */
  code: string;
  domain: string;
  purpose: string;
  scopeHonesty: string;
  expertPosture: string;
  visibility: StudioAgentVisibility;
}

interface VersionDraft {
  inputExpected: ExpectedFieldRow[];
  outputExpected: ExpectedFieldRow[];
  contextDefinition: string;
  explainInstructions: string;
  successCriteria: string;
  hitlRequired: boolean;
  executionMode: StudioExecutionMode;
  autonomyFences: Record<string, unknown>;
  defaultDecisionMode: StudioDecisionMode;
  computeClasses: StudioComputeClass[];
  archetype: string | null;
  industryPackId: string | null;
}

function toAgentDraft(agent: AiStudioAgent): AgentDraft {
  return {
    name: agent.name,
    code: agent.code,
    domain: agent.domain ?? '',
    purpose: agent.purpose ?? '',
    scopeHonesty: agent.scopeHonesty ?? '',
    expertPosture: agent.expertPosture ?? '',
    visibility: agent.visibility,
  };
}

function toVersionDraft(version: AiStudioAgentVersion): VersionDraft {
  // Claim quality = defaultDecisionMode. Execution is always propose+approve (HITL).
  return {
    inputExpected: version.inputExpected,
    outputExpected: version.outputExpected,
    contextDefinition: version.contextDefinition ?? '',
    explainInstructions: version.explainInstructions ?? '',
    successCriteria: version.successCriteria ?? '',
    hitlRequired: true,
    executionMode: 'propose_approve',
    // Apply Other (hard fence) defaults when fences omit `never`.
    autonomyFences: normalizeAutonomyFences(version.autonomyFences),
    defaultDecisionMode: STUDIO_DECISION_MODES.includes(version.defaultDecisionMode)
      ? version.defaultDecisionMode
      : 'PROVISIONAL',
    computeClasses: version.computeClasses,
    archetype: version.archetype,
    industryPackId: version.industryPackId,
  };
}

function versionToRequest(draft: VersionDraft): UpdateVersionRequest {
  return {
    // Spine is derived from outputs (and defaults) — not authored as checkboxes.
    enabledStages: deriveEnabledStages(draft.outputExpected),
    inputExpected: draft.inputExpected,
    outputExpected: draft.outputExpected,
    contextDefinition: draft.contextDefinition.trim() || null,
    explainInstructions: draft.explainInstructions.trim() || null,
    successCriteria: draft.successCriteria.trim() || null,
    // Claim quality = defaultDecisionMode. Execution stays propose+approve (HITL).
    hitlRequired: true,
    executionMode: 'propose_approve',
    autonomyFences: normalizeAutonomyFences(draft.autonomyFences),
    defaultDecisionMode: draft.defaultDecisionMode,
    computeClasses: draft.computeClasses,
    archetype: draft.archetype,
    industryPackId: draft.industryPackId,
  };
}

/** Prefer open draft (Copy / new agent); else newest published, else newest row. */
function pickDefinitionVersion(
  versions: AiStudioAgentVersion[]
): AiStudioAgentVersion | null {
  if (versions.length === 0) return null;
  const draft = versions.find((v) => v.lifecycleStatus === 'draft');
  if (draft) return draft;
  const published = versions.find((v) => v.lifecycleStatus === 'published');
  if (published) return published;
  return versions[0] ?? null;
}

export interface UseAgentDefinitionResult {
  agent: AiStudioAgent | null;
  version: AiStudioAgentVersion | null;
  agentDraft: AgentDraft | null;
  versionDraft: VersionDraft | null;
  packs: AiStudioIndustryPack[];
  loading: boolean;
  saving: boolean;
  dirty: boolean;
  /**
   * True when agent State is not Draft — identity / About fields locked.
   * State select stays editable among private / public / archived.
   */
  agentReadOnly: boolean;
  /** True when the loaded version is not a draft — blueprint locked. */
  blueprintReadOnly: boolean;
  /** @deprecated Prefer agentReadOnly / blueprintReadOnly; true when both locked. */
  readOnly: boolean;
  /** True when agent visibility is not draft — code no longer tracks name. */
  codeLocked: boolean;
  jsonValid: boolean;
  errorKey: string | null;
  /** API validation detail (e.g. release readiness bullets). */
  errorDetail: string | null;
  setAgentField: <K extends keyof AgentDraft>(field: K, value: AgentDraft[K]) => void;
  setVersionField: <K extends keyof VersionDraft>(field: K, value: VersionDraft[K]) => void;
  applyVersionPatch: (patch: Partial<VersionDraft>) => void;
  applyAgentPatch: (patch: Partial<AgentDraft>) => void;
  setJsonValidity: (valid: boolean) => void;
  save: () => Promise<boolean>;
}

export function useAgentDefinition(agentId: string | undefined): UseAgentDefinitionResult {
  const [agent, setAgent] = useState<AiStudioAgent | null>(null);
  const [version, setVersion] = useState<AiStudioAgentVersion | null>(null);
  const [agentDraft, setAgentDraft] = useState<AgentDraft | null>(null);
  const [versionDraft, setVersionDraft] = useState<VersionDraft | null>(null);
  const [agentBaseline, setAgentBaseline] = useState<AgentDraft | null>(null);
  const [versionBaseline, setVersionBaseline] = useState<VersionDraft | null>(null);
  const [packs, setPacks] = useState<AiStudioIndustryPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [jsonValid, setJsonValid] = useState(true);

  useEffect(() => {
    if (!agentId) {
      setLoading(false);
      setErrorKey('aiStudio.agents.notFound');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    setErrorDetail(null);

    (async () => {
      try {
        const [loadedAgent, loadedVersions, loadedPacks] = await Promise.all([
          getAgent(agentId),
          listVersions(agentId),
          listIndustryPacks(),
        ]);
        if (cancelled) return;

        let selected = pickDefinitionVersion(loadedVersions);

        // Brand-new Draft agent: create the first empty version here.
        if (!selected && loadedAgent.visibility === 'draft') {
          selected = await createVersion(agentId);
        }

        if (!selected) {
          setErrorKey('aiStudio.definition.loadFailed');
          return;
        }

        setAgent(loadedAgent);
        setVersion(selected);
        setPacks(loadedPacks);

        const nextAgent = toAgentDraft(loadedAgent);
        const nextVersion = toVersionDraft(selected);
        setAgentDraft(nextAgent);
        setVersionDraft(nextVersion);
        setAgentBaseline(nextAgent);
        setVersionBaseline(nextVersion);
      } catch {
        if (!cancelled) setErrorKey('aiStudio.definition.loadFailed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const dirty = useMemo(() => {
    if (!agentDraft || !versionDraft || !agentBaseline || !versionBaseline) return false;
    return (
      JSON.stringify(agentDraft) !== JSON.stringify(agentBaseline) ||
      JSON.stringify(versionDraft) !== JSON.stringify(versionBaseline)
    );
  }, [agentBaseline, agentDraft, versionBaseline, versionDraft]);

  const agentReadOnly = agentDraft?.visibility !== 'draft';
  const blueprintReadOnly = version?.lifecycleStatus !== 'draft';
  const readOnly = Boolean(agentReadOnly && blueprintReadOnly);
  const codeLocked = agentDraft?.visibility !== 'draft';

  const setAgentField = useCallback(
    <K extends keyof AgentDraft>(field: K, value: AgentDraft[K]) => {
      setAgentDraft((current) => {
        if (!current) return current;
        // Non-draft: only State may change (private ↔ public ↔ archived).
        if (current.visibility !== 'draft' && field !== 'visibility') {
          return current;
        }
        if (
          field === 'visibility' &&
          current.visibility !== 'draft' &&
          value === 'draft'
        ) {
          // Cannot return a released agent to Draft — use Catalog Copy for a new Draft agent.
          return current;
        }
        const next: AgentDraft = { ...current, [field]: value };
        if (field === 'name' && current.visibility === 'draft') {
          next.code = slugifyAgentCode(String(value));
        }
        if (field === 'visibility' && value !== 'draft' && current.visibility === 'draft') {
          next.code = slugifyAgentCode(current.name) || current.code;
        }
        return next;
      });
    },
    []
  );

  const setVersionField = useCallback(
    <K extends keyof VersionDraft>(field: K, value: VersionDraft[K]) => {
      setVersionDraft((current) => {
        if (!current) return current;
        // Blueprint edits only while the open version is still draft.
        return { ...current, [field]: value };
      });
    },
    []
  );

  const applyVersionPatch = useCallback((patch: Partial<VersionDraft>) => {
    setVersionDraft((current) => (current ? { ...current, ...patch } : current));
  }, []);

  const applyAgentPatch = useCallback((patch: Partial<AgentDraft>) => {
    setAgentDraft((current) => {
      if (!current) return current;
      const next = { ...current, ...patch };
      if (patch.name !== undefined && current.visibility === 'draft') {
        next.code = slugifyAgentCode(String(patch.name));
      }
      return next;
    });
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!agentId || !agentDraft || !version?.id || !versionDraft) return false;
    setSaving(true);
    setErrorKey(null);
    setErrorDetail(null);
    try {
      const wasDraftAgent = agentBaseline?.visibility === 'draft';
      const leavingDraft =
        wasDraftAgent && agentDraft.visibility !== 'draft';
      const versionIsDraft = version.lifecycleStatus === 'draft';

      // Persist blueprint before release checks.
      let savedVersion = version;
      if (versionIsDraft && !blueprintReadOnly) {
        savedVersion = await updateVersion(version.id, versionToRequest(versionDraft));
      }

      // Identity while Draft; keep visibility draft until publish succeeds.
      if (wasDraftAgent) {
        const savedAgent = await updateAgent(agentId, {
          name: agentDraft.name.trim(),
          code: agentDraft.code,
          domain: agentDraft.domain.trim() || null,
          purpose: agentDraft.purpose.trim() || null,
          scopeHonesty: agentDraft.scopeHonesty.trim() || null,
          expertPosture: agentDraft.expertPosture.trim() || null,
          visibility: leavingDraft ? 'draft' : agentDraft.visibility,
        });
        setAgent(savedAgent);
        const nextAgent = toAgentDraft(savedAgent);
        setAgentDraft(nextAgent);
        setAgentBaseline(nextAgent);
      } else {
        const savedAgent = await updateAgent(agentId, {
          visibility: agentDraft.visibility,
        });
        setAgent(savedAgent);
        const nextAgent = toAgentDraft(savedAgent);
        setAgentDraft(nextAgent);
        setAgentBaseline(nextAgent);
      }

      // Release: publish validates readiness, freezes version, sets Private when still Draft.
      if (leavingDraft && savedVersion.lifecycleStatus === 'draft') {
        savedVersion = await publishVersion(version.id);
        if (agentDraft.visibility === 'public' || agentDraft.visibility === 'archived') {
          const savedAgent = await updateAgent(agentId, {
            visibility: agentDraft.visibility,
          });
          setAgent(savedAgent);
          const nextAgent = toAgentDraft(savedAgent);
          setAgentDraft(nextAgent);
          setAgentBaseline(nextAgent);
        }
      }

      setVersion(savedVersion);
      const nextVersion = toVersionDraft(savedVersion);
      setVersionDraft(nextVersion);
      setVersionBaseline(nextVersion);
      return true;
    } catch (err) {
      const apiMessage =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { error?: string } } }).response?.data?.error || ''
            ).trim()
          : '';
      setErrorDetail(apiMessage || null);
      setErrorKey('aiStudio.definition.saveFailed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    agentBaseline?.visibility,
    agentDraft,
    agentId,
    blueprintReadOnly,
    version,
    versionDraft,
  ]);

  return {
    agent,
    version,
    agentDraft,
    versionDraft,
    packs,
    loading,
    saving,
    dirty,
    agentReadOnly,
    blueprintReadOnly,
    readOnly,
    codeLocked,
    jsonValid,
    errorKey,
    errorDetail,
    setAgentField,
    setVersionField,
    applyVersionPatch,
    applyAgentPatch,
    setJsonValidity: setJsonValid,
    save,
  };
}
